import type { UsageConflictEntry } from '@testflow/shared';
import { ApiError } from './api.js';

// Stabile Domänen-Codes (aus apps/api/src/routes/*), die einen nutzer-sinnvollen Text verdienen.
// Alles andere fällt bewusst auf errors.generic — nie Rohtext ins UI (analog import-errors.ts).
export const KNOWN_ERROR_CODES: ReadonlySet<string> = new Set([
  'forbidden',
  'invalid_transition',
  'severity_not_in_project',
  'error_type_not_in_project',
  'assignee_not_in_project',
  'defect_not_found',
  'plan_not_found',
  'plan_item_not_found',
  'testcase_not_found',
  // #106: Testfall mit vorhandenen Testläufen ist unlöschbar (Läufe = Testnachweise, 409 in
  // catalog-testcases.ts). Ohne diesen Eintrag fiele der 409 auf errors.generic zurück und der
  // Nutzer erführe den Grund nicht.
  'testcase_has_evidence',
  'failed_step_not_in_testcase',
  'screenshot_too_large',
  'project_not_found',
  'term_exists',
  'term_not_found',
  'customer_not_found',
  'e2e_not_found',
  // #128: Bündel-Abruf der Kettensichten mit kaputter oder zu großer Auswahl.
  'invalid_e2e_ids',
  'process_not_found',
  'step_not_found',
  'testdata_not_found',
  'must_have_one_default',
  // Branch-Review W2 (#55): drei Codes entstanden mit der Mehrere-aktive-Pläne-Auswahl
  // (signoff.ts) und fielen bisher alle auf errors.generic zurück, plus der neue 409-Guard.
  'plan_not_in_project',
  'test_plan_id_required',
  'invalid_test_plan_id',
  'signoff_already_confirmed',
  // #88: Freigabe unter 100 % bestandener Testfälle ohne Kommentar (400 in signoff.ts).
  'signoff_note_required',
  // #52/#51 (Task 6): SAP-Note-Eindeutigkeit + node_id-Validierung im Simplifications-Tab.
  'sap_note_exists',
  'invalid_node_id',
  // Task B2 (#29 Finding 23): geführtes Umhängen statt stiller Null-Setzung bei Severity/
  // Fehlertyp. severity_in_use/error_type_in_use tragen zusätzlich `in_use` (siehe
  // usageConflictEntries unten) — der generische Text hier ist nur der Toast-Fallback, das Panel
  // zeigt die betroffenen Einträge selbst namentlich an.
  'severity_in_use',
  'error_type_in_use',
  'severity_not_found',
  'error_type_not_found',
  'reassign_same_severity',
  'reassign_same_error_type',
  // Review-Fix B1: das Umhängen löscht die Quelle — die Standard-Stufe darf so nicht verschwinden,
  // sonst stünde das Projekt ohne Default da (stille severity_id=null bei jedem neuen Defect).
  'reassign_default_severity',
  // #47 (Task 9): Datenarten-Pflege-Sidepanel. label_required deckt eine Server-Race ab (der
  // Client-Guard verhindert das Absenden schon vorher) — data_kind_in_use ist der eigentliche
  // Fall, den die Zusammenführen-Aktion auflösen soll.
  'data_kind_in_use',
  'data_kind_not_found',
  'label_required',
  // #47 (Task 10, Review-Nachzug aus Task 9): invalid_data_kind ist die 422 bei einem FK-Verstoss
  // beim Anlegen/Ändern von Testdaten (catalog-testdata.ts); merge_same_kind die 422, wenn Quelle
  // und Ziel beim Zusammenführen identisch sind (catalog-data-kinds.ts).
  'invalid_data_kind',
  'merge_same_kind',
  // #47 (Whole-Branch-Review I1): 23505 auf dem partiellen Label-Unique-Index (Rename/konkurrierendes
  // Anlegen) wird jetzt als benannte 409 statt generischem 500 gemeldet (catalog-data-kinds.ts).
  'data_kind_exists',
  // Task 13 (Nutzerverwaltung): 409 beim Ändern der E-Mail auf eine bereits vergebene Adresse,
  // 404 beim PATCH auf einen inzwischen gelöschten User (users.ts).
  'email_already_exists',
  'user_not_found',
  // Bundle A (#130): Platzhalter direkt ersetzen (replace-stub in users.ts) — 409, wenn das Ziel kein
  // Platzhalter (mehr) ist; stub_email_not_allowed, wenn als „echte" Mail wieder eine
  // @stub.invalid-Adresse kommt (Schema-Refine).
  'user_not_stub',
  'stub_email_not_allowed',
  // Bundle A (#130): Platzhalter mit bestehendem Nutzer zusammenführen (merge-into in users.ts) —
  // 409, wenn das Ziel selbst ein Platzhalter ist; 422, wenn Quelle und Ziel derselbe Nutzer sind.
  'merge_target_is_stub',
  'merge_into_self',
  // #23 Teil 2 (Cross-Tenant-FK-Validierung): drei Schreibpfade übernahmen Fremd-IDs im
  // Request-Body ungeprüft — process_id (Testfälle), node_ids (Prozess-Labels), e2e_members
  // (test_case_id).
  'test_case_not_in_customer',
  'process_not_in_customer',
  'node_not_in_project',
  // #153 (Zuordnung → „Neuer Prozess …"): 409, wenn der Name im Kunden schon vergeben ist, und 400,
  // wenn der gewählte Knoten kein Teilprojekt ist (assertNodeKind). Beide treffen den Nutzer im
  // Zielfeld der Zuordnungsseite; ohne Eintrag erführe er nur „Etwas ist schiefgelaufen".
  'process_name_exists',
  'node_not_teilprojekt',
  // #23 Teil 2, Review-Nachzug: die uuid-Systematik über alle Create-/Update-Schemata brachte vier
  // weitere ungeprüfte Fremd-ID-Übernahmen. default_tester_id/tester_id/Vertretungen brauchten
  // user_not_in_project, module_id module_not_in_customer, e2e_id (Testlauf) e2e_not_in_customer.
  // data_kind_id nutzt den schon vorhandenen invalid_data_kind. duplicate_e2e_member ersetzt den
  // 500, den ein doppelt genannter Testfall am Unique-Index (e2e_id, test_case_id) auslöste.
  'user_not_in_project',
  'module_not_in_customer',
  'e2e_not_in_customer',
  'duplicate_e2e_member',
  // #23 Teil 2, Nachprüfung F1: derselbe node_id zweimal in node_ids löste an process_labels
  // (unique auf process_id+node_id) einen 500 aus — jetzt eine benannte 422, analog
  // duplicate_e2e_member.
  'duplicate_process_label',
  // #105 („blockiert außerdem"): 409 beim Verknüpfen — Testfall ist entweder schon als „außerdem
  // betroffen" verlinkt oder ist bereits der primäre Testfall dieses Defects.
  'already_linked',
  'is_primary_case',
  // #114 (A9, Plan-Lebenszyklus): vier Domänencodes aus routes/plans.ts. `plan_closed` ist der
  // häufigste — jeder Schreibversuch am abgeschlossenen Plan endet dort, und ohne diesen Eintrag
  // erführe der Nutzer nur „Es ist ein Fehler aufgetreten" statt des eigentlichen Grundes.
  // `signoffs_open` kommt als JSON-Body mit der Knotenliste; er bekommt hier trotzdem einen Text,
  // weil er über mapApiError auch dann sinnvoll bleiben muss, wenn ihn eine andere Aufrufstelle
  // ohne eigenes Warnfenster erwischt.
  'plan_closed',
  // #131 (Tester per Excel, routes/plan-tester-import.ts): Datei-Fehler der Vorschau — dieselben
  // Codes wie der Import-Wizard (import-errors.ts), hier für die Plan-Seite.
  'tester_import_columns_missing',
  'file_missing',
  'unsupported_file_type',
  'file_too_large',
  'parse_failed',
  'another_plan_active',
  'reopen_note_required',
  'invalid_plan_transition',
  'signoffs_open',
  // 2. Abnahme, Befund 1+2: die beiden Nachweis-Konflikte. Sie kommen als JSON-Body mit
  // run_count/signoff_count (parsePlanEvidence baut daraus den ausführlichen Satz); die Texte hier
  // sind der Fallback für Aufrufstellen ohne eigene Darstellung. `plan_has_evidence` existierte
  // seit #85 im Server, hatte aber nie einen Text — weil es nie eine Aufrufstelle gab.
  'plan_has_evidence',
  'plan_reset_has_evidence',
  // #113 (Task 9): die drei uebrigen Kettencodes des Lauf-Absendens (routes/runs.ts). Sie treffen
  // den Tester im Ausfuehrungsfeld, also genau dort, wo ein roher Code am wenigsten hilft:
  // `predecessor_not_done` (A4, die Sperre), `result_description_required` (A3, der Pflichttext
  // beim gruenen Lauf mit Nachfolger) und `e2e_id_required` (A6, der Lauf nennt seine Kette nicht).
  'predecessor_not_done',
  'result_description_required',
  'e2e_id_required',
  // #113 (Task 8, Review-Nachzug): 422 aus routes/runs.ts — der Testfall ist kein Glied dieser
  // Kette (mehr). Entsteht, wenn zwischen dem Laden der Kettensicht und dem Absenden jemand das
  // Glied aus der Kette bzw. dem Plan-Scope nimmt; ohne Eintrag fiele er auf errors.generic.
  'testcase_not_in_e2e',
  // #126/#186: 422 aus routes/runs.ts — die Wahl im Fehler-Dialog („Testfall bestanden" / „Testfall
  // nicht bestanden") passt nicht zum Ergebnis (veralteter Client oder Doppel-Absenden); der Tester
  // muss die Seite neu laden.
  'stops_chain_mismatch',
  // #208 Nachtrag: notierte Fehler als Grund des Fehler-Laufs (routes/runs.ts). Die beiden ersten
  // entstehen, wenn ein notierter Fehler zwischen Laden und Absenden verknüpft, gelöscht oder fremd
  // wurde; die beiden letzten sind Vertragsfehler eines veralteten Clients.
  'linked_defect_not_noted',
  'linked_defect_changed',
  'linked_defects_require_error',
  'defect_required',
  // #195 (Weitergabe, routes/handover.ts): 409 `plan_not_active` (Plan im Entwurf) und die beiden
  // 422 gegen ein falsches Ziel. `plan_closed` steht oben. Ohne Einträge fiele ein Ziel, das
  // zwischen Laden und Absenden ungültig wurde, auf „Etwas ist schiefgelaufen".
  'plan_not_active',
  'handover_target_invalid',
  'handover_target_self',
]);

export function mapApiError(t: (key: string) => string, err: unknown): string {
  if (!(err instanceof ApiError)) return t('errors.generic');
  const raw = err.message;
  console.error('[api] backend error', err.status, raw);
  // Error-Handler gibt Zod/internal als JSON ({"error":"..."}); HTTPException als Plain-Code.
  if (raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw) as { error?: string };
      if (parsed.error === 'validation_error') return t('errors.validation_error');
      if (parsed.error === 'internal_error') return t('errors.internal_error');
      // Server liefert manche Domaenen-Codes als JSON-Body statt Plain-Code (z. B.
      // sap_note_exists mit angehaengtem `existing`) — die sollen denselben benannten Text
      // bekommen wie ihr Plain-Code-Pendant unten, statt auf generic zu fallen (#52-Review
      // Finding 1, Detail).
      if (parsed.error && KNOWN_ERROR_CODES.has(parsed.error)) return t(`errors.${parsed.error}`);
    } catch {
      // fällt unten auf generic
    }
    return t('errors.generic');
  }
  return KNOWN_ERROR_CODES.has(raw) ? t(`errors.${raw}`) : t('errors.generic');
}

// Task B2 (#29 Finding 23): PUT /config/severity-levels bzw. /config/error-types antwortet 409 mit
// { error: 'severity_in_use' | 'error_type_in_use', in_use: [{ key, label_de, count }] } (JSON-Body
// wie sap_note_exists, s. mapApiError oben). Das Vokabular-Panel braucht die einzelnen Einträge
// namentlich statt nur des generischen Fehlertexts — dieser Helfer entpackt sie, `null` wenn der
// Fehler kein Verwendungskonflikt ist (z. B. Netzwerkfehler, andere 4xx). `UsageConflictEntry`
// kommt aus @testflow/shared (project-config.ts) — dieselbe Zeilenform, mit der der Server antwortet.
export function usageConflictEntries(err: unknown): UsageConflictEntry[] | null {
  if (!(err instanceof ApiError)) return null;
  if (!err.message.startsWith('{')) return null;
  try {
    const parsed = JSON.parse(err.message) as { error?: string; in_use?: UsageConflictEntry[] };
    if (
      (parsed.error === 'severity_in_use' || parsed.error === 'error_type_in_use') &&
      Array.isArray(parsed.in_use)
    ) {
      return parsed.in_use;
    }
  } catch {
    // fällt durch zu null
  }
  return null;
}

// #87: der Router antwortet 400 mit dem PLAIN-TEXT-Body `retest_missing` (HTTPException-Bodies
// sind in diesem Stack kein JSON, s. api-Test `res.text() === 'retest_missing'`) — anders als die
// beiden JSON-kodierten Konflikt-Bodies oben ist hier also ein reiner String-Vergleich richtig.
// Eigener Helfer statt Inline-Vergleich an den beiden Aufrufstellen (board-page.tsx,
// defect-detail.tsx): beide müssen exakt dasselbe erkennen, sonst öffnet der Begründungs-Dialog
// an einer Stelle und an der anderen fällt derselbe Fehler auf den generischen Toast zurück.
export function isRetestMissingError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 400 && err.message === 'retest_missing';
}

// #194: dieselbe Antwortform wie retest_missing (400, Plain-Text-Body): der Melder eines nicht
// blockierenden Defects hat die Behebung noch nicht bestätigt, Schließen verlangt dann `comment`.
// Eigener Helfer neben isRetestMissingError, damit Board und Detail beide Fälle gleich erkennen
// und denselben Begründungs-Dialog öffnen (nur mit anderem Text).
export function isReporterPendingError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 400 && err.message === 'reporter_pending';
}

// #194: 409 `fix_check_not_open` aus POST /my-defects/:id/confirm-fix — der Defect wurde zwischen
// Laden und Klick anders bearbeitet. Für den Prüfer kein Fehler, sondern ein veralteter Stand:
// „Meine Tests" zeigt einen Hinweis und lädt die Karten neu.
export function isFixCheckNotOpenError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.message === 'fix_check_not_open';
}

// Follow-up #194: 409 `invalid_transition` heißt meist „ein anderer war schneller" (der Server
// wechselt nur, wenn der Defect noch im gelesenen Status steht). Board und Detail laden dann neu,
// damit die Karte in ihre echte Spalte springt — der Fehler-Toast bleibt.
export function isInvalidTransitionError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.message === 'invalid_transition';
}

// Final-Review #194: Veraltet ist die Karte auch bei 404 `defect_not_found` — die Vertretung ist
// inzwischen abgelaufen (ihr Prüf- und Leserecht hing daran). Beides ist kein Fehler des Prüfers:
// Hinweis und Neuladen, damit die Karte verschwindet, statt mit Dauerfehler klickbar zu bleiben.
export function isFixCheckStaleError(err: unknown): boolean {
  return (
    isFixCheckNotOpenError(err) ||
    (err instanceof ApiError && err.status === 404 && err.message === 'defect_not_found')
  );
}
